import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaTrash,
} from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://nissi-india.com/api";

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.recordset)) return value.recordset;
  return [];
};

const SupervisorListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const [showFilters, setShowFilters] = useState(true);
  const navigate = useNavigate();

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [searchData, setSearchData] = useState({
    supervisorName: "",
    supervisorCode: "",
  });

  const [nameDDL, setNameDDL] = useState([]);
  const [codeDDL, setCodeDDL] = useState([]);
  const [supervisorData, setSupervisorData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  const loadDDLs = async () => {
    try {
      const [namesRes, codesRes] = await Promise.all([
        axios.get(`${BASE_URL}/supervisors/nameDDL`, {
          params: { branchid: branchId },
          headers: commonHeaders,
        }),
        axios.get(`${BASE_URL}/supervisors/codeDDL`, {
          params: { branchid: branchId },
          headers: commonHeaders,
        }),
      ]);

      setNameDDL(asArray(namesRes.data));
      setCodeDDL(asArray(codesRes.data));
    } catch (err) {
      setNameDDL([]);
      setCodeDDL([]);
      toast.error("Failed to load dropdowns");
    }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/supervisors/list`, {
        params: {
          branchid: branchId,
          code: searchData.supervisorCode || "",
          name: searchData.supervisorName || "",
        },
        headers: commonHeaders,
      });

      setSupervisorData(asArray(res.data));
      setSelectedIds([]);
    } catch (err) {
      setSupervisorData([]);
      toast.error("Failed to load supervisor list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDDLs();
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    loadList();
  };

  const handleAddNew = () => {
    navigate("/PaymentVouchers/SupervisorEntry");
  };

  const handleClearFilters = () => {
    setSearchData({ supervisorName: "", supervisorCode: "" });
    setTimeout(() => loadList(), 0);
  };

  const safeSupervisorData = useMemo(
    () => asArray(supervisorData),
    [supervisorData]
  );

  const allIds = useMemo(
    () => safeSupervisorData.map((x) => x.fld_supervisorid),
    [safeSupervisorData]
  );

  const isAllSelected = useMemo(() => {
    if (allIds.length === 0) return false;
    return allIds.every((id) => selectedIds.includes(id));
  }, [allIds, selectedIds]);

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedIds([]);
    else setSelectedIds(allIds);
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ✅ Update — same navigate-to-edit logic as before, now triggered per-row via the Edit icon
  const handleUpdateRow = (row) => {
    if (!row) {
      toast.error("Selected row not found");
      return;
    }

    navigate("/PaymentVouchers/SupervisorEntry", {
      state: { supervisor: row, mode: "edit" },
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select at least one row to delete");
      return;
    }

    const ok = window.confirm(
      `Delete ${selectedIds.length} selected supervisor(s)?`
    );
    if (!ok) return;

    try {
      if (selectedIds.length === 1) {
        await axios.delete(`${BASE_URL}/supervisors/delete`, {
          params: {
            branchid: branchId,
            supervisorid: selectedIds[0],
          },
          headers: commonHeaders,
        });
      } else {
        await axios.post(
          `${BASE_URL}/supervisors/deleteMany`,
          {
            branchid: branchId,
            ids: selectedIds,
            userId,
          },
          { headers: commonHeaders }
        );
      }

      toast.success("Deleted successfully");
      await loadList();
      await loadDDLs();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SUPERVISOR LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Supervisor Listing
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    Search
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supervisor Name
                  </label>
                  <select
                    value={searchData.supervisorName}
                    onChange={(e) => handleSearchChange("supervisorName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {asArray(nameDDL).map((x) => (
                      <option key={x.fld_supervisorid} value={x.fld_supervisorname}>
                        {x.fld_supervisorname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Supervisor Code
                  </label>
                  <select
                    value={searchData.supervisorCode}
                    onChange={(e) => handleSearchChange("supervisorCode", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {asArray(codeDDL).map((x) => (
                      <option key={x.fld_supervisorid} value={x.fld_supervisorcode}>
                        {x.fld_supervisorcode}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sl.No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Supervisor Code</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Supervisor Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Branch Name</th>

                  {/* EDIT - SECOND LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>

                  {/* SELECT ALL - LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loading ? (
                  <tr>
                    <td colSpan={6} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : safeSupervisorData.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  safeSupervisorData.map((row, idx) => {
                    const checked = selectedIds.includes(row.fld_supervisorid);

                    return (
                      <tr
                        key={row.fld_supervisorid}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {idx + 1}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {row.fld_supervisorcode}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {row.fld_supervisorname}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {row.fld_branchname}
                        </td>

                        {/* EDIT */}
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleUpdateRow(row)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>

                        {/* SELECT */}
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelectOne(row.fld_supervisorid)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* ==================== REMOVE BUTTON ==================== */}
            <div className="p-4">
              <button
                onClick={handleDeleteSelected}
                disabled={selectedIds.length === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                  selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                } bg-red-600 hover:bg-red-700 text-white`}
              >
                 🗑
                Remove
              </button>
            </div>
          </div>

          {/* ==================== TOAST ==================== */}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default SupervisorListing;